import { useState, useEffect, memo } from "react";

// World Clock Component - Apple-style analog clock with ticking second hand
const WorldClock = memo(({ timezone, city, country }) => {
  const [time, setTime] = useState(new Date());
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Get time in the specified timezone
  const getTimeInTimezone = () => {
    const options = { timeZone: timezone, hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false };
    const formatter = new Intl.DateTimeFormat('en-US', options);
    const parts = formatter.formatToParts(time);
    const hours = parseInt(parts.find(p => p.type === 'hour').value);
    const minutes = parseInt(parts.find(p => p.type === 'minute').value);
    const seconds = parseInt(parts.find(p => p.type === 'second').value);
    return { hours, minutes, seconds };
  };
  
  const { hours, minutes, seconds } = getTimeInTimezone();
  
  // Calculate angles for clock hands
  const secondAngle = (seconds / 60) * 360;
  const minuteAngle = ((minutes + seconds / 60) / 60) * 360;
  const hourAngle = ((hours % 12 + minutes / 60) / 12) * 360;
  
  // Format time for digital display
  const formatTime = () => {
    const h = hours % 12 || 12;
    const m = minutes.toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    return `${h}:${m} ${ampm}`;
  };
  
  return (
    <div className="flex flex-col items-center">
      {/* Analog Clock */}
      <div className="relative w-16 h-16 rounded-full bg-white dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 shadow-sm">
        {/* Clock face markers */}
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute w-0.5 h-1.5 bg-zinc-300 dark:bg-zinc-600"
            style={{
              left: '50%',
              top: '4px',
              transformOrigin: '50% 28px',
              transform: `translateX(-50%) rotate(${i * 30}deg)`,
            }}
          />
        ))}
        
        {/* Hour hand */}
        <div
          className="absolute bg-zinc-800 dark:bg-zinc-200 rounded-full"
          style={{
            width: '2px',
            height: '18px',
            left: 'calc(50% - 1px)',
            top: '14px',
            transformOrigin: 'center 18px',
            transform: `rotate(${hourAngle}deg)`,
            transition: 'transform 0.1s ease-out',
          }}
        />
        
        {/* Minute hand */}
        <div
          className="absolute bg-zinc-700 dark:bg-zinc-300 rounded-full"
          style={{
            width: '1.5px',
            height: '22px',
            left: 'calc(50% - 0.75px)',
            top: '10px',
            transformOrigin: 'center 22px',
            transform: `rotate(${minuteAngle}deg)`,
            transition: 'transform 0.1s ease-out',
          }}
        />
        
        {/* Second hand */}
        <div
          className="absolute bg-red-500 rounded-full"
          style={{
            width: '1px',
            height: '26px',
            left: 'calc(50% - 0.5px)',
            top: '6px',
            transformOrigin: 'center 26px',
            transform: `rotate(${secondAngle}deg)`,
          }}
        />
        
        {/* Center dot */}
        <div
          className="absolute w-2 h-2 rounded-full bg-red-500"
          style={{
            left: 'calc(50% - 4px)',
            top: 'calc(50% - 4px)',
          }}
        />
      </div>
      
      {/* Digital time display */}
      <div className="mt-1.5 text-center">
        <p className="text-xs font-medium text-foreground tabular-nums" style={{ fontFamily: 'SF Mono, Menlo, monospace' }}>
          {formatTime()}
        </p>
        <p className="text-[10px] text-muted-foreground leading-tight">{city}</p>
      </div>
    </div>
  );
});

WorldClock.displayName = 'WorldClock';

// World Clocks Container
export const WorldClocks = memo(() => {
  const clocks = [
    { timezone: 'America/New_York', city: 'Miami', country: 'USA' },
    { timezone: 'America/Argentina/Buenos_Aires', city: 'Buenos Aires', country: 'Argentina' },
    { timezone: 'Europe/Kiev', city: 'Kyiv', country: 'Ukraine' },
    { timezone: 'America/Sao_Paulo', city: 'São Paulo', country: 'Brazil' },
  ];
  
  return (
    <div className="flex items-center gap-4">
      {clocks.map((clock) => (
        <WorldClock
          key={clock.city}
          timezone={clock.timezone}
          city={clock.city}
          country={clock.country}
        />
      ))}
    </div>
  );
});

WorldClocks.displayName = 'WorldClocks';

// Dynamic Greeting Component
export const DynamicGreeting = memo(({ userName }) => {
  const [greeting, setGreeting] = useState('');
  
  useEffect(() => {
    const updateGreeting = () => {
      const hour = new Date().getHours();
      if (hour >= 5 && hour < 12) {
        setGreeting('Good morning');
      } else if (hour >= 12 && hour < 17) {
        setGreeting('Good afternoon');
      } else if (hour >= 17 && hour < 21) {
        setGreeting('Good evening');
      } else {
        setGreeting('Good night');
      }
    };
    
    updateGreeting();
    const interval = setInterval(updateGreeting, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);
  
  const firstName = userName?.split(' ')[0] || 'there';
  
  return (
    <h1 className="heading-1 text-foreground">
      {greeting}, {firstName}
    </h1>
  );
});

DynamicGreeting.displayName = 'DynamicGreeting';

export default WorldClock;
